import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import SubtypeToolbox from './SubtypeToolbox'
import SubtypePreview, { PREVIEW_DROPPABLE_ID, type PreviewItem } from './SubtypePreview'
import SubtypePropertyEditor from './SubtypePropertyEditor'
import type {
  ActionModuleSubtypesState,
  SubtypeDefinition,
  SubtypeState,
  SubtypeTranslations,
} from '@/lib/action-modules/registry/types'

interface Props {
  value: ActionModuleSubtypesState
  onChanged: (value: ActionModuleSubtypesState) => void
  translations: SubtypeTranslations
}

// Stable id/editorId for the transient insertion ghost (kept out of the sortable id space).
const PLACEHOLDER_ID = 'subtype-insert-placeholder'
const PLACEHOLDER_EDITOR_ID = -1

// Only register a drop target while the pointer is actually inside a droppable (the preview
// list or one of its rows). Outside the list there is no target, so the insertion gap never
// opens and a drop is a no-op. When inside, fall back to closestCenter for precise row targeting.
const collisionDetection: CollisionDetection = args => {
  const within = pointerWithin(args)
  return within.length > 0 ? closestCenter(args) : within
}

/** Build a fresh subtype from a definition, seeding its default property values. Shared by the
 *  real insert and the drag ghost so both have identical height/content. */
function makeSubtypeState(definition: SubtypeDefinition, editorId: number, index: number): SubtypeState {
  const properties: Record<string, never> = {}
  for (const property of definition.editableProperties) {
    if (property.defaultValue !== undefined) properties[property.name] = property.defaultValue as never
  }
  return { definition, subtype: { id: null }, properties, index, editorId }
}

type ActiveDrag =
  | { kind: 'new'; id: UniqueIdentifier; definition: SubtypeDefinition }
  | { kind: 'existing'; id: UniqueIdentifier; state: SubtypeState }

export default function SubtypesTab({ value, onChanged, translations }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  // A small drag threshold so a plain click on a row selects it instead of starting a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const [active, setActive] = useState<ActiveDrag | null>(null)
  // Index at which a toolbox item, if dropped now, would be inserted (null = not over the list).
  const [insertIndex, setInsertIndex] = useState<number | null>(null)
  // A toolbox drag inserts the subtype at the drop point, so its overlay must not animate back to
  // the chip. Kept separate from `active` and NOT cleared in reset(): onDragEnd nulls `active`
  // before dnd-kit reads the drop animation, so deriving it from `active` would fall back to the
  // default animation. Set once at drag start, it survives through the drop.
  const [suppressDropAnimation, setSuppressDropAnimation] = useState(false)

  const reindex = (list: SubtypeState[]) => list.map((s, i) => ({ ...s, index: i }))

  const insertSubtype = useCallback(
    (definition: SubtypeDefinition, index: number) => {
      const newEditorId = value.subtypes.reduce((max, s) => Math.max(max, s.editorId), 0) + 1
      const next = [...value.subtypes]
      next.splice(index, 0, makeSubtypeState(definition, newEditorId, index))
      onChanged({ ...value, subtypes: reindex(next) })
    },
    [value, onChanged],
  )

  // Where a new item would land, given the current hover target. Compares vertical centers to
  // decide before/after the hovered row.
  const computeInsertIndex = useCallback(
    (event: DragOverEvent): number | null => {
      const { over } = event
      if (!over) return null
      if (over.id === PLACEHOLDER_ID) return insertIndex
      if (over.id === PREVIEW_DROPPABLE_ID) return value.subtypes.length
      const overIndex = value.subtypes.findIndex(s => s.editorId === over.id)
      if (overIndex === -1) return value.subtypes.length
      const activeRect = event.active.rect.current.translated
      const after =
        activeRect && over.rect
          ? activeRect.top + activeRect.height / 2 > over.rect.top + over.rect.height / 2
          : false
      return after ? overIndex + 1 : overIndex
    },
    [value.subtypes, insertIndex],
  )

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current
      if (data?.kind === 'new') {
        setActive({ kind: 'new', id: event.active.id, definition: data.definition as SubtypeDefinition })
        setInsertIndex(null)
        setSuppressDropAnimation(true)
      } else {
        const state = value.subtypes.find(s => s.editorId === event.active.id)
        if (state) setActive({ kind: 'existing', id: event.active.id, state })
        setSuppressDropAnimation(false)
      }
    },
    [value.subtypes],
  )

  const onDragOver = useCallback(
    (event: DragOverEvent) => {
      // Existing rows reorder via the sortable strategy; only the new-item ghost needs driving.
      if (active?.kind === 'new') setInsertIndex(computeInsertIndex(event))
    },
    [active, computeInsertIndex],
  )

  const reset = () => {
    setActive(null)
    setInsertIndex(null)
  }

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { over } = event
      if (active?.kind === 'new') {
        if (insertIndex != null) insertSubtype(active.definition, insertIndex)
      } else if (active?.kind === 'existing' && over && over.id !== active.id && over.id !== PLACEHOLDER_ID) {
        const oldIndex = value.subtypes.findIndex(s => s.editorId === active.id)
        const newIndex =
          over.id === PREVIEW_DROPPABLE_ID
            ? value.subtypes.length - 1
            : value.subtypes.findIndex(s => s.editorId === over.id)
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          onChanged({ ...value, subtypes: reindex(arrayMove(value.subtypes, oldIndex, newIndex)) })
        }
      }
      reset()
    },
    [active, insertIndex, insertSubtype, value, onChanged],
  )

  const selectSubtype = (s: SubtypeState) => onChanged({ ...value, selectedSubtype: s })

  const deleteSubtype = (s: SubtypeState) =>
    onChanged({
      ...value,
      subtypes: value.subtypes.filter(x => x !== s),
      selectedSubtype: value.selectedSubtype === s ? undefined : value.selectedSubtype,
    })

  const onSubtypeChange = (oldState: SubtypeState, newState: SubtypeState) =>
    onChanged({
      ...value,
      subtypes: value.subtypes.map(s => (s === oldState ? newState : s)),
      selectedSubtype: value.selectedSubtype === oldState ? newState : value.selectedSubtype,
    })

  // Rows to render: the real subtypes plus, during a toolbox drag over the list, the ghost.
  const previewItems: PreviewItem[] = value.subtypes.map(s => ({ kind: 'subtype', id: s.editorId, state: s }))
  if (active?.kind === 'new' && insertIndex != null) {
    const ghost = makeSubtypeState(active.definition, PLACEHOLDER_EDITOR_ID, insertIndex)
    previewItems.splice(insertIndex, 0, { kind: 'placeholder', id: PLACEHOLDER_ID, state: ghost })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={reset}
    >
      {/* Fixed to the tab's height (see ActionModuleEditor): the single lg row fills the area
          and each column is allowed to shrink (min-h-0) so it scrolls internally instead of
          growing the whole editor. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-3 lg:grid-rows-1">
        <div className="flex min-h-0 flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">{t('tabSubtypes.toolbox')}</h3>
          <SubtypeToolbox />
        </div>
        <div className="flex min-h-64 flex-col gap-2 lg:min-h-0">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">{t('tabSubtypes.preview')}</h3>
          <SubtypePreview
            items={previewItems}
            selected={value.selectedSubtype}
            onSelect={selectSubtype}
            onDelete={deleteSubtype}
          />
        </div>
        <div className="flex min-h-0 flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">{t('tabSubtypes.settings')}</h3>
          <SubtypePropertyEditor
            selectedSubtype={value.selectedSubtype}
            subtypes={value.subtypes}
            translations={translations}
            onChange={onSubtypeChange}
          />
        </div>
      </div>

      {/* Floating preview that follows the pointer, showing the actual subtype preview (what will
          land in the list) for both a toolbox drag and an existing-row reorder. For a toolbox drag
          the drop animation is disabled: the subtype is inserted at the drop point, so animating the
          overlay back to the toolbox chip would read as it flying away. */}
      <DragOverlay dropAnimation={suppressDropAnimation ? null : undefined}>
        {active?.kind === 'new' ? (
          <OverlayRow state={makeSubtypeState(active.definition, PLACEHOLDER_EDITOR_ID, insertIndex ?? 0)} />
        ) : active?.kind === 'existing' ? (
          <OverlayRow state={active.state} />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

/** The floating preview of a subtype row being reordered. Solid card + shadow so it reads
 *  against the phone screen while dragging. */
function OverlayRow({ state }: { state: SubtypeState }) {
  const { t } = useTranslation('action-modules-wizard')
  const name = state.properties.name as string | undefined

  return (
    <div className="rounded-md border bg-card p-2 shadow-lg">
      {name && <p className="mb-1 text-sm font-medium">{name}</p>}
      {state.definition.editorPreview(state, t)}
    </div>
  )
}
